import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LifeBuoy, RotateCcw, Mail, Phone, MapPin, FileText } from "lucide-react";
import { useHeaderFooter } from "@/lib/headerFooter";
import RichBody from "@/components/cms/RichBody";
import { toast } from "sonner";

const TABS = [
  { id: "", label: "Help", icon: LifeBuoy },
  { id: "returns", label: "Returns", icon: RotateCcw },
  { id: "contact", label: "Contact Us", icon: Mail },
];

export default function HelpPage() {
  const { topic = "" } = useParams();
  const navigate = useNavigate();
  const s = useSettings();
  const store = s?.store || {};
  const hf = useHeaderFooter();
  const pages = (hf?.footer?.columns || []).flatMap((c) => c.links || []).filter((l) => l.content && (l.link || "").startsWith("/help/"))
    .map((l) => ({ id: l.link.slice(6), label: l.label, title: l.page_title || l.label, content: l.content, icon: FileText }));
  const tabs = [...TABS, ...pages.filter((p) => !TABS.some((t) => t.id === p.id))];
  const page = pages.find((p) => p.id === topic);
  useEffect(() => { window.scrollTo(0, 0); }, [topic]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <section className="bg-steel text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <span className="text-xs font-mono tracking-[0.3em] text-accent">HELP CENTER</span>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold" data-testid="help-heading">
            {page?.title || tabs.find((t) => t.id === topic)?.label || "Help"}
          </h1>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1 grid md:grid-cols-4 gap-8">
        <nav className="md:col-span-1 space-y-1">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`help-nav-${t.id || "help"}`}
              onClick={() => navigate(`/help${t.id ? "/" + t.id : ""}`)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                topic === t.id ? "bg-steel text-white" : "bg-white border hover:border-accent"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>

        <div className="md:col-span-3 space-y-5 text-sm leading-relaxed text-slate-700">
          {topic === "" && (
            <div className="rounded-xl border bg-white p-6" data-testid="help-content">
              <h2 className="font-heading font-bold text-xl mb-3">How can we help?</h2>
              <p className="whitespace-pre-line">{s?.help_text || "Loading…"}</p>
            </div>
          )}

          {topic === "returns" && (
            <div className="rounded-xl border bg-white p-6" data-testid="returns-content">
              <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-3"><RotateCcw size={20} className="text-accent" /> Returns &amp; Refunds</h2>
              <p className="whitespace-pre-line">{s?.returns_text || "Loading…"}</p>
              {store.contact_email && <p className="mt-4">To start a return, email <a className="text-accent hover:underline" href={`mailto:${store.contact_email}`}>{store.contact_email}</a>.</p>}
            </div>
          )}

          {topic === "contact" && <ContactBlock store={hf?.footer?.contact ? { contact_email: hf.footer.contact.email, contact_phone: hf.footer.contact.phone, address: hf.footer.contact.address } : store} />}

          {page && (
            <div className="rounded-xl border bg-white p-6" data-testid={`help-page-${page.id}`}>
              <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-3"><FileText size={20} className="text-accent" /> {page.title}</h2>
              {/<[a-z][\s\S]*>/i.test(page.content)
                ? <RichBody html={page.content} testid="help-page-content" />
                : <p className="whitespace-pre-line" data-testid="help-page-content">{page.content}</p>}
            </div>
          )}
          {!page && hf && !["", "returns", "contact"].includes(topic) && (
            <div className="rounded-xl border bg-white p-6" data-testid="help-page-missing">This help page isn't available. Choose a topic on the left.</div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ContactBlock({ store }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error("Please enter your name, email and message");
    setBusy(true);
    try {
      await api.post("/enquiries", { ...f, product_id: "", sku: "" });
      toast.success("Message sent — ACP will be in touch shortly");
      setF({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Could not send your message"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="rounded-xl border bg-white p-6">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-3"><Mail size={20} className="text-accent" /> Get in touch</h2>
        <div className="space-y-2 text-sm">
          {store.contact_email?.trim() && <p className="flex items-center gap-2" data-testid="contact-info-email"><Mail size={16} className="text-accent" /> <a className="text-accent hover:underline" href={`mailto:${store.contact_email}`}>{store.contact_email}</a></p>}
          {store.contact_phone?.trim() && <p className="flex items-center gap-2" data-testid="contact-info-phone"><Phone size={16} className="text-accent" /> <a className="text-accent hover:underline" href={`tel:${store.contact_phone.replace(/\s/g, "")}`}>{store.contact_phone}</a></p>}
          {store.address?.trim() && <p className="flex items-center gap-2" data-testid="contact-info-address"><MapPin size={16} className="text-accent" /> {store.address}</p>}
        </div>
      </div>
      <form onSubmit={submit} data-testid="contact-form" className="rounded-xl border bg-white p-6 space-y-3">
        <h2 className="font-heading font-bold text-xl mb-1">Send us a message</h2>
        <div><Label>Name *</Label><Input data-testid="contact-name" value={f.name} onChange={set("name")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Email *</Label><Input data-testid="contact-email" value={f.email} onChange={set("email")} /></div>
          <div><Label>Phone</Label><Input data-testid="contact-phone" value={f.phone} onChange={set("phone")} /></div>
        </div>
        <div><Label>Message *</Label><Textarea data-testid="contact-message" rows={4} value={f.message} onChange={set("message")} /></div>
        <Button type="submit" data-testid="contact-submit" disabled={busy} className="w-full bg-accent text-steel hover:bg-amber-600 font-semibold">
          {busy ? "Sending…" : "Send Message"}
        </Button>
      </form>
    </div>
  );
}
